import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Tenant } from '@mtm/auth';
import { MtmAuthBridgeService, MtmAuthComponent } from '@mtm/auth-ui-angular';

/**
 * The workspace picker, rendered as `mtm-auth`'s own `tenant` step so the
 * surface matches the sign-in card the user just came from.
 *
 * No output bindings by design — and in particular NOT `authCodeGenerated`,
 * which the previous `mtm-tenant-select` wiring forwarded to
 * `environment.authProcessUrl`: `mtm-auth` exchanges the code it mints itself
 * and hands the application token straight to `MTM_ON_APP_TOKEN_READY`, which
 * is what navigates. Forwarding it would spend a one-shot code twice, and the
 * `/auth/process` hop is no longer needed from here at all.
 */
@Component({
  selector: 'mtm-tenant-select-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MtmAuthComponent],
  template: `<mtm-auth initialStep="tenant" [initialEmail]="email" [tenants]="tenantRows()" />`,
  styles: `
    :host {
      display: block;
      min-height: 100vh;
    }
  `,
})
export class TenantSelectPageComponent implements OnInit {
  private readonly _bridge = inject(MtmAuthBridgeService);
  private readonly _destroyRef = inject(DestroyRef);

  /** Fills the step's locked-email chip, read from the SDK's in-memory identity. */
  readonly email = this._bridge.getAuthUserSync()?.email ?? '';

  readonly tenantRows = signal<{ id: string; name: string }[]>([]);

  ngOnInit(): void {
    // The login callback passes the tenants it already resolved, so the common
    // case costs no request. A hard refresh arrives with no router state at all,
    // so fall back to fetching rather than rendering an empty picker.
    const preloaded = history.state?.tenants as Tenant[] | undefined;
    if (preloaded?.length) {
      this.tenantRows.set(toRows(preloaded));
      return;
    }

    this._bridge
      .getTenants$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (tenants: Tenant[]) => this.tenantRows.set(toRows(tenants)),
      });
  }
}

/** `mtm-auth` takes plain `{ id, name }` rows, not the wire `Tenant` shape. */
function toRows(tenants: Tenant[]): { id: string; name: string }[] {
  return tenants.map((tenant: Tenant) => ({ id: tenant.tenantUuid, name: tenant.tenantName }));
}
